import MemoryService from '@/services/memories';
import type { Memory } from '@/types/memories';
import { StandardResponse } from '@/types/common';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';

export const memoriesKeys = {
  all: ['my-memories'] as const,
  lists: () => [...memoriesKeys.all, 'list'] as const,
  list: (filters: Record<string, any>) =>
    [...memoriesKeys.lists(), { ...filters }] as const,
};

export const useMyMemories = (filters?: Record<string, any>) => {
  return useQuery<StandardResponse<Memory>>({
    queryKey: memoriesKeys.list(filters ?? {}),
    queryFn: () => MemoryService.getMyMemories(filters),
  });
};

export const useDeleteMemory = () => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, number, { previous: StandardResponse<Memory> | undefined }>({
    mutationFn: (id: number) => MemoryService.deleteMyMemory(id),
    onMutate: async (deletedId) => {
      await queryClient.cancelQueries({ queryKey: memoriesKeys.lists() });
      const queryCache = queryClient.getQueryCache();
      const queries = queryCache.findAll({ queryKey: memoriesKeys.lists() });
      const activeQuery = queries[0];
      const previous = activeQuery
        ? (queryClient.getQueryData(activeQuery.queryKey) as StandardResponse<Memory> | undefined)
        : undefined;

      // Optimistically remove from all list caches
      queryClient.setQueriesData<StandardResponse<Memory>>(
        { queryKey: memoriesKeys.lists() },
        (old) => {
          if (!old) return old;
          const results = Array.isArray(old) ? old : old.results ?? [];
          const filtered = results.filter((m: Memory) => m.id !== deletedId);
          if (Array.isArray(old)) return filtered as any;
          return { ...old, count: filtered.length, results: filtered };
        }
      );

      return { previous };
    },
    onSuccess: () => {
      toast.success('Memory deleted');
    },
    onError: (_err, _id, context) => {
      // Rollback on error
      if (context?.previous) {
        queryClient.setQueriesData<StandardResponse<Memory>>(
          { queryKey: memoriesKeys.lists() },
          () => context.previous
        );
      }
      toast.error('Failed to delete memory');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: memoriesKeys.lists() });
    },
  });
};
